import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import MoonMark from "@/components/MoonMark";
import TextSizeControl from "@/components/TextSizeControl";

const API = process.env.REACT_APP_BACKEND_URL;

const LINKS = [
  { to: "/", label: "Intro", id: "nav-link-intro" },
  { to: "/portfolio", label: "Portfolio", id: "nav-link-portfolio" },
  { to: "/services", label: "Services", id: "nav-link-services" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const { pathname } = useLocation();

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      setMe(res.ok ? await res.json() : null);
    } catch {
      setMe(null);
    }
  };

  useEffect(() => {
    checkAuth();
    const onUpdate = () => checkAuth();
    window.addEventListener("nox-auth-changed", onUpdate);
    return () => window.removeEventListener("nox-auth-changed", onUpdate);
  }, []);

  const signOut = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // best effort
    }
    localStorage.removeItem("nox_admin_token");
    setMe(null);
    window.dispatchEvent(new Event("nox-auth-changed"));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-line bg-ink/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <MoonMark className="h-9 w-9 text-gold transition-transform duration-700 group-hover:rotate-[20deg]" />
            <span className="font-display text-base md:text-lg tracking-[0.3em] text-silver">
              NOX&nbsp;HARMONIA
            </span>
          </Link>

          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.35em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-slate-400 hover:text-silver"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {me ? (
              <>
                <NavLink
                  to="/account"
                  data-testid="nav-link-account"
                  className={({ isActive }) =>
                    `text-[11px] uppercase tracking-[0.35em] transition-colors duration-300 ${
                      isActive ? "text-gold" : "text-slate-400 hover:text-silver"
                    }`
                  }
                >
                  My Quotes
                </NavLink>
                <button
                  onClick={signOut}
                  data-testid="nav-signout"
                  className="cursor-pointer text-[11px] uppercase tracking-[0.35em] text-slate-500 transition-colors duration-300 hover:text-silver"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <NavLink
                to="/auth"
                data-testid="nav-link-signin"
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.35em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-slate-400 hover:text-silver"
                  }`
                }
              >
                Sign In
              </NavLink>
            )}
            <TextSizeControl />
            <Link to="/commission" data-testid="nav-commission-cta" className="btn-night !px-5 !py-2.5">
              Commission Custom Music
            </Link>
          </nav>

          <div className="flex items-center gap-3 md:hidden">
            <TextSizeControl testId="textsize-btn-mobile" />
            <button
              className="text-silver"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-12 bg-ink/95 backdrop-blur-2xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={l.to}
                  data-testid={`${l.id}-mobile`}
                  className="font-display text-3xl tracking-[0.25em] text-silver"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24, duration: 0.5 }}
              className="flex items-center gap-8"
            >
              {me ? (
                <>
                  <Link to="/account" data-testid="nav-link-account-mobile" className="font-display text-2xl tracking-[0.25em] text-gold">
                    My Quotes
                  </Link>
                  <button
                    onClick={signOut}
                    data-testid="nav-signout-mobile"
                    className="cursor-pointer text-[11px] uppercase tracking-[0.35em] text-slate-500"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link to="/auth" data-testid="nav-link-signin-mobile" className="font-display text-2xl tracking-[0.25em] text-gold">
                  Sign In
                </Link>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Link to="/commission" className="btn-night" data-testid="nav-mobile-commission">
                Commission Custom Music
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
